import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePasswordRecovery } from "hooks/accountManagement/useAccountRecovery";
import SecurityQuestions from "./securityList";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import logo from "../../../assets/img/illustrations/logo.png";
import { toast } from "react-toastify";

const RECOVERY_METHOD = {
  OTP: "EMAIL_OR_PHONE",
  SECURITY: "SECURITY_QUESTIONS",
};

const RESEND_COOLDOWN_SECONDS = 10;

export default function AccountSecurityQuestions() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const method = state?.method || RECOVERY_METHOD.SECURITY;
  const recoveryId = state?.recoveryId;
  const questions = state?.questions || [];
  const identifier = state?.identifier || "";
  const initialOtpToken = state?.otpToken || null;
  const maskedDestination = state?.maskedDestination || "";
  const channel = state?.channel || null;

  const { verifyAnswers, verifyRecoveryCode, startRecoveryCode, loading, error } =
    usePasswordRecovery();
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState(initialOtpToken);
  const [localError, setLocalError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (method !== RECOVERY_METHOD.OTP) return undefined;
    if (resendCooldown <= 0) return undefined;

    const intervalId = setInterval(() => {
      setResendCooldown((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [method, resendCooldown]);

  const channelLabel = useMemo(() => {
    if (!channel) return "email/phone";
    return String(channel).toUpperCase() === "EMAIL" ? "email" : "phone";
  }, [channel]);

  if (method === RECOVERY_METHOD.OTP && (!identifier || !otpToken)) {
    return (
      <div className="text-center mt-5 text-danger">
        Invalid recovery session. Please start again.
      </div>
    );
  }

  if (method !== RECOVERY_METHOD.OTP && (!recoveryId || questions.length === 0)) {
    return (
      <div className="text-center mt-5 text-danger">
        Invalid recovery session. Please start again.
      </div>
    );
  }

  const handleSubmit = async (payload) => {
    try {
      const data = await verifyAnswers(payload);
      toast.success("Security answers verified successfully.");
      navigate("/auth/split/reset-password", { state: { resetToken: data.resetToken } });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to verify security answers.");
      console.error("Verification failed:", err.response?.data || err.message);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (!otp.trim() || otp.trim().length < 4) {
      setLocalError("Please enter your verification code.");
      return;
    }

    try {
      const data = await verifyRecoveryCode({ otpToken, code: otp.trim() });
      toast.success("Code verified successfully.");
      navigate("/auth/split/reset-password", {
        state: { resetToken: data.resetToken },
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Invalid verification code.");
    }
  };

  const handleResendCode = async () => {
    if (loading || resendCooldown > 0) return;
    setLocalError("");
    try {
      const data = await startRecoveryCode({ identifier });
      setOtpToken(data?.otpToken || null);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success("Verification code resent.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to resend verification code.");
    }
  };

  const handleCancel = () => {
    navigate("/auth/login");
  };

  if (method === RECOVERY_METHOD.OTP) {
    return (
      <Card.Body>
        <div className="min-vh-100 bg-white d-flex flex-column align-items-center justify-content-center px-3">
          <div className="mb-4">
            <img src={logo} alt="Logo" style={{ height: "56px", objectFit: "contain" }} />
          </div>

          <div className="bg-white rounded-3 shadow-sm p-4" style={{ maxWidth: "420px", width: "100%" }}>
            <div className="text-center mb-4">
              <h4 className="fw-bold mb-1">Enter Verification Code</h4>
              <p className="text-muted small mb-0">
                We sent a code to <strong>{maskedDestination || `your ${channelLabel}`}</strong>.
              </p>
            </div>

            {(localError || error) && (
              <Alert variant="danger" className="small py-2">
                {localError || error}
              </Alert>
            )}

            <Form onSubmit={handleVerifyOtp} className="mt-3">
              <Form.Group className="mb-3">
                <Form.Label className="small fw-medium mb-0">Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  disabled={loading}
                  style={{ height: "44px" }}
                />
              </Form.Group>

              <div className="text-center mb-3">
                <span
                  role="button"
                  tabIndex={loading || resendCooldown > 0 ? -1 : 0}
                  onClick={loading || resendCooldown > 0 ? undefined : handleResendCode}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !loading && resendCooldown <= 0) {
                      e.preventDefault();
                      handleResendCode();
                    }
                  }}
                  className={loading || resendCooldown > 0 ? "text-muted" : "text-success"}
                  style={{
                    cursor: loading || resendCooldown > 0 ? "not-allowed" : "pointer",
                    textDecoration: "underline",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  }}
                >
                  {resendCooldown > 0
                    ? `Resend Code in ${resendCooldown}s`
                    : "Resend Code"}
                </span>
              </div>

              <Button
                variant="success"
                type="submit"
                className="w-100 fw-semibold"
                disabled={loading || otp.trim().length < 4}
                style={{ backgroundColor: "#28a745", border: "none", height: "46px" }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </Button>
            </Form>

            <div className="text-center mt-3">
              <Button
                variant="link"
                onClick={handleCancel}
                disabled={loading}
                className="text-muted small"
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </Card.Body>
    );
  }

  return (
    <Card.Body>
      <SecurityQuestions
        recoveryId={recoveryId}
        questions={questions}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
        error={error}
      />
    </Card.Body>
  );
}
